import React from 'react';
import { Form, Input, Card, Button, Row, Col } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { notEmptyRule } from './validationUtils';
import ImageDisplayField from '../ImageDisplayField';

const { TextArea } = Input;

const ItemsSection = ({ 
  sectionKey, 
  sectionData, 
  uploadingImage, 
  uploadProps, 
  handleImageUploadForItem,
  handleImageUploadForItemIcon,
  currentLanguage,
  form
}) => {
  const { t } = useTranslation();

  const sampleItem = sectionData.items?.[0] || {};
  
  // Create default structure for new items
  const createDefaultItem = () => {
    const defaultItem = {};
    if (sampleItem.title !== undefined) defaultItem.title = '';
    if (sampleItem.image !== undefined) defaultItem.image = '';
    if (sampleItem.icon !== undefined) defaultItem.icon = '';
    if (sampleItem.button !== undefined) defaultItem.button = '';
    if (sampleItem.description !== undefined) defaultItem.description = '';
    if (sampleItem.advantages !== undefined) defaultItem.advantages = [];
    return defaultItem;
  };
  const langKey = currentLanguage.toLowerCase();
  
  return (
    <Form.List name={['content', langKey, sectionKey, 'items']}>
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name, ...restField }) => (
            <Card
              key={key}
              size='small'
              style={{ marginBottom: 16 }}
              extra={
                fields.length > 1 && (
                  <Button
                    type='text'
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(name)}
                    size='small'
                  >
                    {t('Rimuovi')}
                  </Button>
                )
              }
            >
              {sampleItem.title !== undefined && (
                <Form.Item
                  {...restField}
                  name={[name, 'title']}
                  label={t('Titolo')}
                  rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
                >
                  <Input placeholder={t('Inserisci il titolo')} />
                </Form.Item>
              )}

              {sampleItem.image !== undefined && (
                <ImageDisplayField
                  name={[name, 'image']}
                  label={t('Immagine')}
                  uploadingImage={uploadingImage}
                  uploadProps={uploadProps}
                  onUpload={(file) => handleImageUploadForItem(file, sectionKey, name)}
                  onRemove={() => {
                    const currentValues = form.getFieldsValue();
                    const fieldPath = ['content', langKey, sectionKey, 'items', name, 'image'];
                    let current = currentValues;
                    for (let i = 0; i < fieldPath.length - 1; i++) {
                      if (!current[fieldPath[i]]) {
                        current[fieldPath[i]] = {};
                      }
                      current = current[fieldPath[i]];
                    }
                    current[fieldPath[fieldPath.length - 1]] = '';
                    form.setFieldsValue(currentValues);
                  }}
                  rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
                />
              )}

              {sampleItem.icon !== undefined && (
                <Form.Item
                  {...restField}
                  name={[name, 'icon']}
                  label={t('Icona')}
                  rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
                >
                  <ImageDisplayField
                    label={t('Icona')}
                    uploadingImage={uploadingImage}
                    uploadProps={uploadProps}
                    onUpload={(file) => handleImageUploadForItemIcon(file, sectionKey, name)}
                    onRemove={() => {
                      const currentValues = form.getFieldsValue();
                      const fieldPath = ['content', langKey, sectionKey, 'items', name, 'icon'];
                      let current = currentValues;
                      for (let i = 0; i < fieldPath.length - 1; i++) {
                        if (!current[fieldPath[i]]) {
                          current[fieldPath[i]] = {};
                        }
                        current = current[fieldPath[i]];
                      }
                      current[fieldPath[fieldPath.length - 1]] = '';
                      form.setFieldsValue(currentValues);
                    }}
                  />
                </Form.Item>
              )}

              {sampleItem.button !== undefined && (
                <Form.Item
                  {...restField}
                  name={[name, 'button']}
                  label={t('Testo del pulsante')}
                  rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
                >
                  <Input placeholder={t('Inserisci il testo del pulsante')} />
                </Form.Item>
              )}

              {sampleItem.description !== undefined && (
                <Form.Item
                  {...restField}
                  name={[name, 'description']}
                  label={t('Descrizione')}
                >
                  <TextArea rows={2} placeholder={t('Inserisci la descrizione')} />
                </Form.Item>
              )}

              {sampleItem.advantages !== undefined && (
                <Form.Item
                  {...restField}
                  name={[name, 'advantages']}
                  label={t('Vantaggi')}
                >
                  <Form.List name={[name, 'advantages']}>
                    {(advantageFields, { add: addAdvantage, remove: removeAdvantage }) => (
                      <>
                        {advantageFields.map(({ key: advantageKey, name: advantageName, ...advantageRestField }) => (
                          <Row gutter={8} key={advantageKey} style={{ marginBottom: 8 }}>
                            <Col flex='auto'>
                              <Form.Item
                                {...advantageRestField}
                                name={[advantageName, 'title']}
                                label={t('Titolo vantaggio')}
                                rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
                              >
                                <Input placeholder={t('Inserisci il titolo del vantaggio')} />
                              </Form.Item>
                            </Col>
                            <Col flex='none'>
                              {advantageFields.length > 1 && (
                                <Button
                                  type='text'
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => removeAdvantage(advantageName)}
                                  size='small'
                                >
                                  {t('Rimuovi')}
                                </Button>
                              )}
                            </Col>
                          </Row>
                        ))}
                        <Button
                          type='dashed'
                          icon={<PlusOutlined />}
                          onClick={() => addAdvantage({ title: '' })}
                          size='small'
                        >
                          {t('Aggiungi vantaggio')}
                        </Button>
                      </>
                    )}
                  </Form.List>
                </Form.Item>
              )}
            </Card>
          ))}
          <Button
            type='dashed'
            icon={<PlusOutlined />}
            onClick={() => add(createDefaultItem())}
            style={{ width: '100%' }}
          >
            {t('Aggiungi elemento')}
          </Button>
        </>
      )}
    </Form.List>
  );
};

export default ItemsSection; 